import { Link } from 'react-router-dom';
import { useScrollAnimation, useParallax } from '@/hooks/useScrollAnimation';

const reasons = [
  {
    icon: 'ri-time-line',
    title: 'Atención 24/7',
    desc: 'Servicio médico permanente todos los días del año, incluyendo feriados y fines de semana.',
  },
  {
    icon: 'ri-hospital-line',
    title: 'Instalaciones modernas',
    desc: 'Equipamiento de última generación y consultorios diseñados para tu comodidad y seguridad.',
  },
  {
    icon: 'ri-team-line',
    title: 'Equipo especializado',
    desc: 'Médicos y enfermeras altamente calificados en más de 15 especialidades médicas.',
  },
];

export default function WhyChooseSection() {
  const { ref, isVisible } = useScrollAnimation();
  const badge = useParallax(-0.08);

  return (
    <section className="py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-background-50 relative overflow-hidden">
      <div className="absolute bottom-10 right-0 w-72 h-72 bg-primary-100/40 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: text + numbered features */}
          <div ref={ref} className={`lg:col-span-6 reveal-left ${isVisible ? 'is-in' : ''}`}>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-primary-500 uppercase mb-4">
              <span className="w-8 h-px bg-primary-400" />
              ¿Por qué elegirnos?
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-950 mb-6 leading-[1.08]">
              Tu salud en las <span className="italic text-primary-600">mejores manos</span>
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-9 max-w-lg">
              Miles de familias en Tarija confían en nosotros. Esto es lo que nos diferencia de cualquier otro centro médico.
            </p>

            <div className="space-y-4">
              {reasons.map((reason, i) => (
                <div
                  key={reason.title}
                  className="group flex items-start gap-5 p-5 rounded-2xl hover:bg-background-100 border border-transparent hover:border-background-200 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-heading text-2xl font-bold text-background-300 group-hover:text-accent-500 transition-colors w-8">
                      0{i + 1}
                    </span>
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
                      <i className={`${reason.icon} text-xl`} />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">
                      {reason.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {reason.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-9">
              <Link
                to="/contacto"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Agenda tu consulta
                <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          </div>

          {/* Right: image */}
          <div className={`lg:col-span-6 relative reveal-right ${isVisible ? 'is-in' : ''}`}>
            <div className="relative">
              <div className="absolute -top-5 -right-5 bottom-10 left-8 rounded-[2.5rem] bg-primary-100/60 -z-10" />
              <div className="rounded-[2.5rem] overflow-hidden border-4 border-background-50">
                <img
                  src="https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/751a4ba43261092a8c20ee9dd233057a.png"
                  alt="Equipo médico de Clínica Tarija Vitalmedic SRL"
                  className="w-full h-[360px] md:h-[520px] object-cover object-top"
                />
              </div>

              <div
                ref={badge.ref}
                className="absolute -bottom-6 -left-3 md:-left-8 bg-secondary-950 rounded-2xl px-6 py-5"
                style={{ transform: `translateY(${badge.offset}px)` }}
              >
                <p className="font-heading text-3xl md:text-4xl font-bold text-accent-500 leading-none">99%</p>
                <p className="text-xs text-background-300 mt-1.5">Pacientes satisfechos</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}